import React, { useEffect, useState } from 'react';
import api from '../../../api';
import AttendanceAddModal from './modals/Attendance/AttendanceAddModal';
import Pagination from '../Tools/Pagination';
import { format, parseISO } from 'date-fns';
import AttendanceHistoryModal from './modals/Attendance/AttendanceHistoryModal';

// Component to fetch and store date and time formats in sessionStorage
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/ems/get-system-setting');
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date and time formats:', err);
        // Fallback to defaults if API fails
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []); // Run once on mount

  return null; // No UI to render
};

// AttendanceTab component for managing employee attendance records
const AttendanceTab = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    emp_id: '',
    todaystatus: '',
    date: new Date().toISOString().split('T')[0],
    area_id: '',
    location_id: '',
    medical_center_id: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAttendanceId, setCurrentAttendanceId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [filters, setFilters] = useState({
    emp_id: '',
    attendance_status: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [currentHistoryEmp, setCurrentHistoryEmp] = useState(null); 

  // Get date and time format from sessionStorage
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  // Utility function to format date based on settings
   const formatAttendanceDate = (dateString) => {
     if (!dateString) return 'N/A';
   
     try {
       const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
       const timeFormat = sessionStorage.getItem('time_format') || '24';
   
       // Map your backend formats to date-fns
       const dateFormatMap = {
         'DD/MM/YYYY': 'dd/MM/yyyy',
         'MM/DD/YYYY': 'MM/dd/yyyy',
         'YYYY-MM-DD': 'yyyy-MM-dd',
       };
   
       const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
       const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
   
       let utcDate;
   
       if (dateString.includes('T')) {
         // ISO format: 2025-10-27T16:27:19.000000Z
         utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
       } else {
         // MySQL format: 2025-10-27 16:27:19
         utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
       }
   
       // Extract UTC parts manually to avoid local timezone shift
       const formatted = format(
         new Date(
           utcDate.getUTCFullYear(),
           utcDate.getUTCMonth(),
           utcDate.getUTCDate(),
           utcDate.getUTCHours(),
           utcDate.getUTCMinutes(),
           utcDate.getUTCSeconds()
         ),
         formatString
       );
   
       return formatted;
     } catch (err) {
       console.error('Error formatting alarm time:', err, dateString);
       return 'Invalid Date';
     }
   };

  // Fetch attendance records
  const fetchAttendance = async () => {
    try {
      setIsLoading(true);
      const roleName = sessionStorage.getItem('Role_Name');
      const userId = sessionStorage.getItem('user_id') || '';
      const params = {
        user_id: roleName === 'EMS Resource Manager' ? userId : ''
      };

      const response = await api.get('/ems/attendance', { params });
      const rawData = response.data || [];

      const attendanceWithDetails = await Promise.all(
        rawData.map(async (employee) => {
          const attendance = employee.todays_attendance || {};
          let areaName = 'N/A';
          let locationName = 'N/A';
          let medicalCenterName = 'N/A';
          const areaId = attendance.area_id || employee.area_id;
          const locationId = employee.location_id;
          const medicalCenterId = employee.medical_center_id;

          // Fetch area details if area_id exists
          if (areaId) {
            try {
              const areaRes = await api.get(`/ems/areas/${areaId}`);
              areaName = areaRes?.data?.area_name || 'N/A';
            } catch (err) {
              console.warn(`Failed to fetch area for ID ${areaId}`);
            }
          }

          // Fetch location details if location_id exists
          if (locationId) {
            try {
              const locationRes = await api.get(`/ems/locations/${locationId}`);
              locationName = locationRes?.data?.location_name || 'N/A';
            } catch (err) {
              console.warn(`Failed to fetch location for ID ${locationId}`);
            }
          }

          // Fetch medical center details if medical_center_id exists
          if (medicalCenterId) {
            try {
              const medicalCenterRes = await api.get(`/ems/medical-centers/${medicalCenterId}`);
              medicalCenterName = medicalCenterRes?.data?.name || 'N/A';
            } catch (err) {
              console.warn(`Failed to fetch medical center for ID ${medicalCenterId}`);
            }
          }

          return {
            id: attendance.id || null,
            emp_id: employee.emp_id,
            employee: {
              first_name: employee.first_name,
              last_name: employee.last_name
            },
            today_status: attendance.today_status ?? null,
            area_id: areaId,
            area_name: areaName,
            location_id: locationId,
            location_name: locationName,
            medical_center_id: medicalCenterId,
            medical_center_name: medicalCenterName,
            date: attendance.created_at || employee.created_at
          };
        })
      );

      setAttendanceData(attendanceWithDetails);
      setFilteredData(attendanceWithDetails);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      alert('Failed to fetch attendance data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = (empId) => {
  setShowHistoryModal(true);
  setHistoryData([]); // reset before fetch
  setCurrentHistoryEmp(empId); // <-- add this state
};

  // Apply filters
  const applyFilters = () => {
    let data = [...attendanceData];
    
    if (filters.emp_id) {
      data = data.filter(d => d.emp_id?.toString().includes(filters.emp_id));
    }
    
    if (filters.attendance_status) {
      if (filters.attendance_status === '1') {
        data = data.filter(d => d.today_status === 1);
      } else if (filters.attendance_status === '0') {
        data = data.filter(d => d.today_status === 0);
      } else if (filters.attendance_status === 'not_marked') {
        data = data.filter(d => d.today_status === null);
      }
    }
    
    setFilteredData(data);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ emp_id: '', attendance_status: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditAttendance = (record) => {
    setIsEditMode(true);
    setCurrentAttendanceId(record.id);
    setFormData({
      emp_id: record.emp_id,
      todaystatus: record.today_status?.toString() || '',
      date: record.date || '',
      area_id: record.area_id || '',
      location_id: record.location_id || '',
      medical_center_id: record.medical_center_id || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        emp_id: formData.emp_id,
        today_status: formData.todaystatus === '' ? null :
          formData.todaystatus === '1' ? 1 : 0,
        area_id: formData.area_id,
        location_id: formData.location_id,
        medical_center_id: formData.medical_center_id
      };

      if (isEditMode) {
        if (currentAttendanceId) {
          await api.delete(`/ems/attendance/${currentAttendanceId}`);
        }
        await api.post('/ems/attendance', payload);
        alert('Attendance record updated successfully!');
      } else {
        await api.post('/ems/attendance', payload);
        alert('Attendance record added successfully!');
      }
      
      fetchAttendance();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'add'} attendance record. ${error.response?.data?.message || ''}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this attendance record?')) {
      try {
        await api.delete(`/ems/attendance/${id}`);
        alert('Attendance record deleted successfully!');
        fetchAttendance();
      } catch (error) {
        console.error('Error deleting attendance:', error);
        alert('Failed to delete attendance record');
      }
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowSubmitButton(true);
    } else {
      setSelectedFile(null);
      setShowSubmitButton(false);
    }
  };

  // Upload attendance CSV
  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Use sessionStorage token or fixed token
      const token = sessionStorage.getItem("access_token") 
        || "1314|duIQEokwqMx6AFvZ5PF2xniaPZBThULoHb7kcfSW562e6756";

      const response = await api.post(
        "/ems/import_csv_for_attendance",
        formData
      );

      alert("CSV imported successfully. Processed rows.");
      console.log("File upload response:", response.data);
      fetchAttendance();
      setSelectedFile(null);
      setShowSubmitButton(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert(error.response?.data?.message || "Failed to upload file. Please try again.");
    }
  };

  useEffect(() => { 
    fetchAttendance(); 
  }, []);

  useEffect(() => { 
    applyFilters(); 
  }, [filters, attendanceData]);

  const totalItems = filteredData.length;
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );

  return (
    <div className="tab-pane active" id="employee" role="tabpanel">
      <DateTimeFormatFetcher />
      <div className="filter-search mt-3">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Employee ID" 
                name="emp_id" 
                value={filters.emp_id} 
                onChange={handleFilterChange} 
              />
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <select 
                className="form-control form-select" 
                name="attendance_status" 
                value={filters.attendance_status} 
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="1">Present</option>
                <option value="0">Absent</option>
                <option value="not_marked">Not Marked</option>
              </select>
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <button 
                type="button" 
                className="btn mb-1 btn-outline-primary btn-rounded" 
                onClick={clearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>

          {/* File Upload and Submit Buttons */}
          <div className="d-flex flex-column gap-2">
            {!showSubmitButton ? (
              <label className="btn btn-primary mb-0">
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </label>
            ) : (
              <>
                <span className="text-muted">Selected File: {selectedFile?.name}</span>
                <button
                  className="btn btn-primary mb-0"
                  onClick={handleFileUpload}
                >
                  Submit
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="py-3 px-0">
        {isLoading ? (
          <div className="text-center py-4">Loading attendance...</div>
        ) : (
          <>
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th><h6 className="fw-semibold mb-0">Employee ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Name</h6></th>
                    <th><h6 className="fw-semibold mb-0">Status</h6></th>
                    <th><h6 className="fw-semibold mb-0">Location</h6></th>
                    <th><h6 className="fw-semibold mb-0">Area</h6></th>
                    <th><h6 className="fw-semibold mb-0">Medical Center</h6></th>
                    <th><h6 className="fw-semibold mb-0">Date</h6></th>
                    <th><h6 className="fw-semibold mb-0">Action</h6></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? paginatedData.map((record) => (
                    <tr key={record.id || record.emp_id}>
                      <td>{record.emp_id || 'EMP-000'}</td>
                      <td>{record.employee.first_name} {record.employee.last_name}</td>
                      <td>
                        <span className={`round-8 rounded-circle me-2 d-inline-block ${
                          record.today_status === null ? 'text-bg-secondary' : 
                          record.today_status === 1 ? 'text-bg-success' : 'text-bg-danger'
                        }`}></span>
                        {record.today_status === null ? 'Not Marked' : 
                         record.today_status === 1 ? 'Present' : 'Absent'}
                      </td>
                      <td>{record.location_name || 'N/A'}</td>
                      <td>{record.area_name || 'N/A'}</td>
                      <td>{record.medical_center_name || 'N/A'}</td>
                      <td>{record.date ? formatAttendanceDate(record.date) : 'N/A'}</td>
                      <td>
                        <button 
                          className="h4 me-3 border-0 bg-transparent" 
                          onClick={() => handleEditAttendance(record)} 
                          title="Edit"
                        >
                          <i className="ti ti-edit text-warning"></i>
                        </button>
                        <button
                          className="h4 me-3 border-0 bg-transparent"
                          onClick={() => handleViewHistory(record.emp_id)}
                          title="History"
                        >
                          <i className="ti ti-history text-info"></i>
                        </button>
                        {record.id && (
                          <button 
                            className="h4 me-3 border-0 bg-transparent" 
                            onClick={() => handleDelete(record.id)} 
                            title="Delete"
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        No attendance records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination 
              total={totalItems}
              limit={itemsPerPage} 
              page={currentPage} 
              onPageChange={setCurrentPage} 
            />
          </>
        )}
      </div>

      <AttendanceAddModal
        show={showModal}
        onClose={() => setShowModal(false)}
        formData={formData}
        onChange={handleInputChange}
        onSave={handleSave}
        isEditMode={isEditMode}
        isLoading={isLoading}
      />

        <AttendanceHistoryModal
          show={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          empId={currentHistoryEmp}
          formatAttendanceDate={formatAttendanceDate}
        />
    </div>
  );
};

export default AttendanceTab;